// @flow
import React, { useCallback, useState } from 'react';
import { FormatOptionLabelMeta, type OptionType } from '@atlaskit/select';
import styled from 'styled-components';
import { each } from 'lodash';
import { withApollo, WithApolloClient } from 'react-apollo';
import CustomAsyncSelect, { CustomAsyncSelectProps } from './CustomAsyncSelect';
import { VIRTUAL_COMPONENT_RELEASES_AUTO_COMPLETE_QUERY } from '../../graphql/queries';

const MenuOptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 7px;
`;

export const parseOptions = (versions) => {
    const options = [
        {
            label: 'UNRELEASED',
            options: [],
        },
        {
            label: 'RELEASED',
            options: [],
        },
    ];

    each(versions, (version) => {
        const { name, isReleased, componentName, id } = version;
        options[isReleased ? 1 : 0].options.push({
            label: name,
            value: id,
            componentName,
        });
    });
    return options;
};

export const formatOptionLabel = (option: OptionType, { context }: FormatOptionLabelMeta) => {
    if (context === 'menu') {
        return (
            <MenuOptionWrapper>
                <div>
                    <strong>{option.label}</strong>
                </div>
                {option.componentName ? <div style={{ fontSize: 12 }}>{option.componentName}</div> : null}
            </MenuOptionWrapper>
        );
    }

    return option.label;
};

export const load = async (client: ApolloClient, projectId?: string, searchText: string, excluded: string[]) => {
    const result = await client.query({
        query: VIRTUAL_COMPONENT_RELEASES_AUTO_COMPLETE_QUERY,
        variables: {
            filter: {
                name: searchText?.length ? searchText : undefined,
                projects: [projectId],
                excluded,
            },
            pagination: {
                pageSize: searchText?.length ? 50 : 10,
            },
            sorting: {
                property: `name`,
                direction: `ASC`,
            },
        },
        fetchPolicy: 'network-only',
    });

    return parseOptions(result.data.virtualComponentReleasesPaginated.rows);
};

export interface Props extends CustomAsyncSelectProps {
    projectId?: string;
}

const VirtualComponentVersionSelect = ({ client, projectId, onChange, ...props }: WithApolloClient<Props>) => {
    const [selected, setSelected] = useState(Array.isArray(props.value) ? props.value.map(({ value }) => value) : []);

    const onChangeHandler = useCallback(
        (newValue: any, actionMeta: ActionMeta<any>) => {
            setSelected(Array.isArray(newValue) ? newValue.map(({ value }) => value) : []);
            return onChange?.(newValue, actionMeta);
        },
        [onChange],
    );

    const loadOptions = useCallback(
        (searchText: string) => load(client, projectId, searchText, selected),
        [client, projectId, selected],
    );

    return (
        <CustomAsyncSelect
            loadOptions={loadOptions}
            cacheOptions
            formatOptionLabel={formatOptionLabel}
            onChange={onChangeHandler}
            defaultOptions
            {...props}
        />
    );
};

export default withApollo(VirtualComponentVersionSelect);
